import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import {
    Table,
    TableRow,
    TableCell,
    TableContainer,
    TableHead,
    TableBody,
    Switch,
    Button,
    Snackbar,
    Backdrop,
    CircularProgress,
    Alert,
    Box,
    Card,
    CardHeader,
    CardContent,
} from "@mui/material";

// API services
import {
    fetchAdminSettings,
    updateAdminSettings, // Assuming this API updates settings
} from "features/admin/services/adminService";

import AdminSettingFormModal from "./components/AdminSettingFormModal";

const Admin = () => {
    const [settingData, setSettingData] = useState({
        key: "",
        value: "",
        description: "",
        status: "inactive",
    });
    const [openModal, setOpenModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);
    const [data, setData] = useState([]);

    // Fetch Admin Settings
    const fetchGetAdminSettings = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchAdminSettings();
            setData(response?.data?.items || []);
        } catch (err) {
            setError("Failed to fetch admin settings. Please try again.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchGetAdminSettings();
    }, [fetchGetAdminSettings]);

    // Input change handler
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSettingData((prev) => ({ ...prev, [name]: value }));
    };

    // Open edit modal
    const handleEdit = (setting) => {
        setSettingData(setting);
        setIsEditing(true);
        setOpenModal(true);
    };

    // Close modal
    const handleClose = () => {
        setOpenModal(false);
        setSettingData({
            key: "",
            value: "",
            description: "",
            status: "inactive",
        });
    };

    // Submit form (Update setting)
    const handleSubmit = async () => {
        try {
            setLoading(true);

            // console.log("Updating setting", settingData);
            if (!settingData?.id) {
                setError("Invalid setting data.");
                return;
            }

            // 🔹 Call API to update setting
            // const response = await updateAdminSettings({
            //     id: settingData.id,
            //     // value: {
            //         // key: settingData.key,
            //         value: settingData.value,
            //         // description: settingData.description,
            //         // status: settingData.status,
            //     // },
            // });
            const response = await updateAdminSettings(settingData.id, {
                value: settingData.value, 
            });
            if (response?.success) {
                setSuccess(response?.message || "Setting updated successfully.");
                fetchGetAdminSettings();
            } else {
                setError(response?.message || "Failed to update setting.");
            }

            setOpenModal(false);
        } catch (err) {
            console.error(err);
            setError("Something went wrong while updating setting.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <Card sx={{ mt: 3 }}>
                <CardHeader
                    title="Admin Settings"
                    // 🟡 Hidden Add Button
                    action={
                        // <Button variant="contained" color="primary" onClick={handleAdd}>
                        //   Add Setting
                        // </Button>
                        null
                    }
                />
                <CardContent>
                    <TableContainer>
                        <Table>
                            {/* 🟡 Commented Table Head */}

                            {/* <TableHead> */}
                            <TableRow>
                                <TableCell style={{ fontWeight: "bold" }}>ID</TableCell>
                                <TableCell style={{ fontWeight: "bold" }}>Key</TableCell>
                                <TableCell style={{ fontWeight: "bold" }}>Value</TableCell>
                                <TableCell style={{ fontWeight: "bold" }}>Description</TableCell>
                                <TableCell style={{ fontWeight: "bold" }}>Status</TableCell>
                                <TableCell style={{ fontWeight: "bold" }}>Created At</TableCell>
                                <TableCell style={{ fontWeight: "bold" }}>Updated At</TableCell>
                                <TableCell style={{ fontWeight: "bold" }}>Actions</TableCell>
                            </TableRow>
                            {/* </TableHead> */}


                            <TableBody>
                                {data.map((item) => (
                                    <TableRow key={item.id} hover>
                                        <TableCell>{item.id}</TableCell>
                                        <TableCell>{item.key}</TableCell>
                                        <TableCell>{item.value}</TableCell>
                                        <TableCell>{item.description}</TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={item.status === "active"}
                                                color="primary"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {new Date(item.createdAt).toLocaleString("en-GB")}
                                        </TableCell>
                                        <TableCell>
                                            {new Date(item.updatedAt).toLocaleString("en-GB")}
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" gap={1}>
                                                <Button
                                                    onClick={() => handleEdit(item)}
                                                    color="primary"
                                                    size="small"
                                                >
                                                    Edit
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Modal for Edit */}
            <AdminSettingFormModal
                open={openModal}
                handleClose={handleClose}
                settingData={settingData}
                handleInputChange={handleInputChange}
                handleSubmit={handleSubmit}
                isEditing={isEditing}
            />

            <Footer />

            {/* Snackbar */}
            <Snackbar
                open={!!error || !!success}
                autoHideDuration={3000}
                onClose={() => {
                    setError(null);
                    setSuccess(null);
                }}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={() => {
                        setError(null);
                        setSuccess(null);
                    }}
                    severity={error ? "error" : "success"}
                    sx={{ width: "100%" }}
                >
                    {error || success}
                </Alert>
            </Snackbar>

            {/* Loading Spinner */}
            <Backdrop
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </DashboardLayout>
    );
};

export default Admin;
